import React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {
  DataObjectId,
  extractStickerState,
  Hub,
  ReportDataField,
  ReportFieldKey,
  StickerStateId,
  Tag,
  TaskData,
  User
} from 'yougile-model'
import {Sync} from '@client/sync'
import {UserAvatarV2} from '@components/common/user-avatar-v2/user-avatar-v2'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {makeTaskDataService} from '@extensions/task-data-helper'

type CustomCell = (task: TaskData) => React.ReactNode

const bem = Util.bemFrom('stats-reports-data-field')

const format = (timestamp: number, noTime?: boolean) =>
  timestamp
    ? Time.formatCustom(timestamp, noTime ? 'DD.MM.YYYY' : 'DD.MM.YYYY hh:mm')
    : ''

const userElFromId = (userId: DataObjectId): JSX.Element => {
  if (!userId) {
    return <span />
  }
  const user = Sync.getCurrentSandbox().get(userId) as User
  const prox = UserProxy.create(user)
  if (!prox) {
    return <span />
  }
  return (
    <span className={bem('user')}>
      <UserAvatarV2
        {...prox}
        size={16}
        hideStatus={true}
        className={bem('user-ava')}
      />
      {prox.userName}
    </span>
  )
}

const multiUserEl = (users: DataObjectId[]): JSX.Element => {
  if (users.length === 0) {
    return <span />
  } else if (users.length === 1) {
    const user = Sync.getCurrentSandbox().get(users[0]) as User
    const prox = UserProxy.create(user)
    if (!prox) {
      return <span />
    }
    return (
      <span className={bem('user')}>
        <UserAvatarV2
          {...prox}
          size={16}
          hideStatus={true}
          className={bem('user-ava')}
        />
        {prox.userName}
      </span>
    )
  } else {
    return (
      <>
        {users.map(userId => {
          const user = Sync.getCurrentSandbox().get(userId) as User
          const prox = UserProxy.create(user)
          if (!prox) {
            return <span />
          }
          return (
            <span className={bem('user')} key={userId}>
              <UserAvatarV2
                {...prox}
                size={16}
                hideStatus={true}
                className={bem('user-ava')}
              />
            </span>
          )
        })}
      </>
    )
  }
}

const titleClick = (id: DataObjectId) => () => {
  const box = Sync.getCurrentSandbox()
  const hub = box.get(id) as Hub
  if (hub) {
    ChatUtils.toggleChat(hub)
  }
}

const customCells: Map<ReportFieldKey, CustomCell> = new Map()
customCells.set('title', t => {
  const box = Sync.getCurrentSandbox()
  const task = box.getHub(t.id)
  const title = task ? Task.getTitle(t.id) : t.title
  return (
    <span
      className={cn(
        'flex items-center',
        bem('title', {
          complete: !!t.completeTime,
          uncomplete: !t.completeTime
        })
      )}
      onClick={titleClick(t.id)}
    >
      {title}
    </span>
  )
})

customCells.set('id-task', t => {
  const idTask = idTasksApi.getIdTask(t.id)
  return (
    <span
      className={cn('flex items-center', bem('title'))}
      onClick={titleClick(t.id)}
    >
      {idTask || ' '}
    </span>
  )
})

customCells.set('createdBy', t => userElFromId(t.createdBy))
customCells.set('assignedTo', t => multiUserEl(t.assigned))
customCells.set('responsible', t => userElFromId(t.assigned?.[0]))
customCells.set('deadline', t => {
  const deadline = extractStickerState(t.deadline)
  return (
    <span
      className={bem('deadline', {
        warn:
          !!deadline &&
          !t.completeTime &&
          DeadlineCtl.isSpoiled(
            {
              deadline,
              withTime: t.deadlineWithTime
            },
            {taskData: t}
          )
      })}
    >
      {format(deadline, !t.deadlineWithTime) || ''}
    </span>
  )
})

const getStickerCustomCell = (stickerId: DataObjectId): CustomCell => {
  const box = Sync.getCurrentSandbox()
  const sticker = box.get(stickerId) as Tag
  const type = Sticker.getType(sticker)
  const getState = (val: StickerStateId): {name: string; color?: string} => {
    if (type === 'numeric' || type === 'text') {
      return {name: val ? `${val}` : ''}
    }

    if (type === 'string') {
      return stickerCtl.String.getState(sticker, val) || {name: ''}
    } else if (type === 'sprint') {
      return stickerCtl.Sprint.getState(sticker, val) || {name: ''}
    } else {
      return {name: ''}
    }
  }

  return t => {
    const val = t.stickers[stickerId]
    const state = getState(val)
    return <span style={{color: state.color}}>{state.name}</span>
  }
}

export const renderCell = (
  field: ReportDataField,
  task: TaskData
): React.ReactNode => {
  if (customCells.has(field(Lang.msg).key)) {
    return customCells.get(field(Lang.msg).key)(task)
  } else if (field(Lang.msg).key.split('sticker:').length === 2) {
    const stickerId = field(Lang.msg).key.split('sticker:')[1] as DataObjectId
    const cell = getStickerCustomCell(stickerId)
    return cell(task)
  } else {
    const taskDataService = makeTaskDataService(Sync.get(task))
    return field(Lang.msg).getText(task, taskDataService)
  }
}
